import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faRocket, faLink, faCopy, faSackDollar, faArrowsRotate } from '@fortawesome/pro-light-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Input } from '@shared/components/ui/input';
import { useTranslation } from 'react-i18next';
import { useAffiliatesTotals, useAffiliateCode } from '../../hooks/useAffiliatesTotals';
import { useRef } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { CreateCode } from '.';
import { CreateCodeModal, CreateCodeModalRef } from '../modals/CreateCodeModal';
import { formatCurrencyAmount, useCurrency } from '../../../../Orders/shared/hooks/useCurrency';

const Benefits = () => {
  const { t } = useTranslation('affiliatesModule');
  const { data: totals, isLoading: loadingTotals } = useAffiliatesTotals();
  const { data: codeData } = useAffiliateCode();
  const inputRef = useRef<HTMLInputElement>(null);
  const apiBaseUrl = import.meta.env.VITE_API_WH;
  const cleanBaseUrl = apiBaseUrl.replace(/\/api\/v1\/?$/, '');
  const baseUrl = `${cleanBaseUrl}/register?ref=`;
  const affiliateCode = codeData?.code || codeData?.name || '';
  const affiliateUrl = `${baseUrl}${encodeURIComponent(affiliateCode)}`;
  const { toast } = useToast();
  const currency = useCurrency();
  const benefits = [
    {
      icon: faSackDollar,
      title: t('benefits.guaranteed.title'),
      desc: t('benefits.guaranteed.desc'),
    },
    {
      icon: faArrowsRotate,
      title: t('benefits.recurring.title'),
      desc: t('benefits.recurring.desc'),
    },
    {
      icon: faEye,
      title: t('benefits.transparency.title'),
      desc: t('benefits.transparency.desc'),
    },
    {
      icon: faRocket,
      title: t('benefits.easy.title'),
      desc: t('benefits.easy.desc'),
    },
  ];
  const hasAffiliateCode = !!(codeData?.code || codeData?.name);
  const createCodeModalRef = useRef<CreateCodeModalRef>(null);
  const handleOpenCreateCodeModal = () => {
    createCodeModalRef.current?.open(() => {
      // Aquí puedes manejar la creación del código
      // Por ahora solo cerrar la modal
    });
  };
  return (
    <div className="w-full mt-8 border-b border-card-border pb-6 mb-8">
      <h3 className="text-xl font-semibold text-center mb-8">{t('benefits.title')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {benefits.map((b, i) => (
          <SectionBox key={i}>
            <div className="bg-card-bg light:bg-bg-info w-10 h-10 rounded-lg mb-4 inline-flex items-center justify-center">
              <FontAwesomeIcon icon={b.icon} className="text-primary text-sm" />
            </div>
            <div>
              <div className="font-semibold mb-1">{b.title}</div>
              <div className="text-sm text-complementary-text">{b.desc}</div>
            </div>
          </SectionBox>
        ))}
      </div>

      {hasAffiliateCode ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {/* Mi código de afiliado */}
          <SectionBox>
            <div className="font-semibold mb-2">{t('affiliateCode.title')}</div>
            <div className="relative mb-2">
              {/* Icono de enlace a la izquierda */}
              <span className="absolute left-3 top-1/2 -translate-y-1/2">
                <FontAwesomeIcon icon={faLink} className="text-sm" />
              </span>
              <Input ref={inputRef} type="text" value={affiliateUrl} readOnly className="px-10" />
              {/* Botón copiar a la derecha */}
              <button
                className="absolute right-1 top-1/2 -translate-y-1/2 py-1 px-2 rounded hover:bg-hover-press transition-colors"
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.select();
                    document.execCommand('copy');
                    toast({
                      title: t('affiliateCode.copiedTitle'),
                      description: t('affiliateCode.copiedDesc'),
                    });
                  }
                }}
                title={t('affiliateCode.copy')}
                type="button"
              >
                <FontAwesomeIcon icon={faCopy} className="text-sm" />
              </button>
            </div>
            <div className="text-sm text-complementary-text">{t('affiliateCode.help')}</div>
          </SectionBox>
          {/* Resumen de ganancias */}
          <SectionBox className="flex flex-col">
            <div className="font-semibold mb-2">{t('earningsSummary.title')}</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-complementary-text">{t('earningsSummary.totalCommissions')}</span>
                <span className="text-primary font-medium text-xl">
                  {loadingTotals ? '...' : (totals?.total_commissions ?? 0)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-complementary-text">{t('earningsSummary.totalEarned')}</span>
                <span className="text-success font-medium text-xl">
                  {loadingTotals ? '...' : formatCurrencyAmount(totals?.total_commission_amount ?? 0, currency)}
                </span>
              </div>
            </div>
          </SectionBox>
        </div>
      ) : (
        <>
          <CreateCode onCreate={handleOpenCreateCodeModal} />
          <CreateCodeModal ref={createCodeModalRef} />
        </>
      )}
    </div>
  );
};

export default Benefits;
